import type { PayloadAction, SliceCaseReducers } from "@reduxjs/toolkit";
import type {
  SpecsLayerModelPath,
  SpecsLayerSideIndexesPatch,
  SpecsEditorNav,
  SpecsEditorSession,
  SpecsInput,
  SpecsLayerView,
} from "../../../../containers/SpecsEditor/types";
import {
  createDesignNav,
  createElementsNav,
  createDecorationsHistoryNav,
  createDecorationsNav,
  createFamiliesHistoryNav,
  createFamiliesNav,
  createFabricsHistoryNav,
  createFabricsNav,
  createGlobalNav,
  createHistoryNav,
  createImportNav,
  createLayerSpecsNav,
  createMetadataNav,
  createPartsNav,
  createPositionsNav,
  createScopedNav,
  createSettingsNav,
  createShippingNav,
  createStylesNav,
  generateId,
  PositionNavPayload,
  touch,
} from "../helpers";
import {
  allImages,
  createDefaultOutputImageSide,
  getOutputImageSide,
  setOutputImageSide,
} from "./images";

export function defaultSession(): SpecsEditorSession {
  return {
    id: generateId(),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    input: null,
    backInput: null,
    layerRuntimeByKey: {},
    layerRuntimeOrder: [],
    previewColor: "#4a7cdc",
    referenceImageUrl: null,
    falSettings: {
      apiKey: "",
      resolution: "1K" as string,
      aspectRatio: "1:1" as string,
      outputFormat: "png" as string,
    },
    nav: {
      kind: "empty",
    },
    layerView: "preview",
    hoveredLayerId: null,
    iconPickerTarget: null,
  };
}

export const sessionReducers = {
  setInput(state, { payload }: PayloadAction<SpecsInput>) {
    state.input = payload;
    if (payload.layers.length === 1) {
      const layerId = payload.layers[0].id;
      for (const image of allImages(state)) {
        const front = getOutputImageSide(image, "front");
        if (front && !front.sourceLayerId) {
          front.sourceLayerId = layerId;
          setOutputImageSide(image, "front", front);
        }
      }
    }
    touch(state);
  },

  setBackInput(state, { payload }: PayloadAction<SpecsInput | null>) {
    state.backInput = payload;
    if (payload) {
      const singleLayerId =
        payload.layers.length === 1 ? payload.layers[0].id : null;
      for (const image of allImages(state)) {
        const back =
          getOutputImageSide(image, "back") ??
          createDefaultOutputImageSide(image.role);
        if (singleLayerId && !back.sourceLayerId) {
          back.sourceLayerId = singleLayerId;
        }
        setOutputImageSide(image, "back", back);
      }
    }
    touch(state);
  },

  setNav(state, { payload }: PayloadAction<SpecsEditorNav>) {
    state.nav = payload;
  },

  setLayerSideIndexes(
    state,
    { payload }: PayloadAction<Record<string, SpecsLayerSideIndexesPatch>>
  ) {
    for (const [layerId, sideIndexes] of Object.entries(payload)) {
      const layer = state.layerRuntimeByKey[layerId];
      if (layer) {
        layer.sideIndexes = sideIndexes;
      }
    }
  },

  setLayerModelPaths(
    state,
    { payload }: PayloadAction<Record<string, SpecsLayerModelPath | null>>
  ) {
    for (const [layerId, path] of Object.entries(payload)) {
      const runtime = state.layerRuntimeByKey[layerId];
      if (runtime && path) {
        runtime.modelLayerPath = {
          partName: path.partName,
          elementName: path.elementName,
          styleName: path.styleName,
          layerName: path.layerName ?? runtime.modelLayerPath.layerName,
        };
      }
    }
  },

  selectSettings(state) {
    state.nav = createSettingsNav(state);
  },

  selectImport(state) {
    state.nav = createImportNav(state);
  },

  selectHistory(state) {
    state.nav = createHistoryNav(state);
  },

  selectPositions(state, { payload }: PayloadAction<PositionNavPayload>) {
    state.nav = createPositionsNav(state, payload);
  },

  selectMetadata(state) {
    state.nav = createMetadataNav(state);
  },

  selectParts(state, { payload }: PayloadAction<string | undefined>) {
    state.nav = createPartsNav(state, payload);
  },

  selectElements(state) {
    state.nav = createElementsNav(state);
  },

  selectStyles(state) {
    state.nav = createStylesNav(state);
  },

  selectLayerSpecs(state) {
    state.nav = createLayerSpecsNav(state);
  },

  selectShipping(state) {
    state.nav = createShippingNav(state);
  },

  selectGlobal(state) {
    state.nav = createGlobalNav(state);
  },

  selectFamilies(state, { payload }: PayloadAction<string | undefined>) {
    state.nav = createFamiliesNav(state, payload);
  },

  selectFabrics(state, { payload }: PayloadAction<string | undefined>) {
    state.nav = createFabricsNav(state, payload);
  },

  selectDecorations(state, { payload }: PayloadAction<string | undefined>) {
    state.nav = createDecorationsNav(state, payload);
  },

  selectFamiliesHistory(state, { payload }: PayloadAction<string | undefined>) {
    state.nav = createFamiliesHistoryNav(state, payload);
  },

  selectFabricsHistory(state, { payload }: PayloadAction<string | undefined>) {
    state.nav = createFabricsHistoryNav(state, payload);
  },

  selectDecorationsHistory(
    state,
    { payload }: PayloadAction<string | undefined>
  ) {
    state.nav = createDecorationsHistoryNav(state, payload);
  },

  selectDesign(state) {
    state.nav = createDesignNav(state);
  },

  selectElement(
    state,
    {
      payload,
    }: PayloadAction<{
      partName: string;
      elementName: string;
      styleName?: string;
    }>
  ) {
    state.nav =
      state.nav.kind === "positions"
        ? createPositionsNav(state, {
            positionId: state.nav.positionId,
            partName: payload.partName,
            elementName: payload.elementName,
            styleName: payload.styleName,
          })
        : createScopedNav(
            state,
            payload.partName,
            payload.elementName,
            payload.styleName
          );
  },

  setActiveStyle(
    state,
    {
      payload,
    }: PayloadAction<{
      partName: string;
      elementName: string;
      styleName: string;
    }>
  ) {
    state.nav =
      state.nav.kind === "positions"
        ? createPositionsNav(state, {
            positionId: state.nav.positionId,
            partName: payload.partName,
            elementName: payload.elementName,
            styleName: payload.styleName,
          })
        : createScopedNav(
            state,
            payload.partName,
            payload.elementName,
            payload.styleName
          );
    touch(state);
  },

  setLayerView(state, { payload }: PayloadAction<SpecsLayerView>) {
    state.layerView = payload;
  },

  setHoveredLayer(state, { payload }: PayloadAction<string | null>) {
    state.hoveredLayerId = payload;
  },

  resetRuntimeState(state) {
    const apiKey = state.falSettings.apiKey;
    const input = state.input;
    const backInput = state.backInput;
    const fresh = defaultSession();
    Object.assign(state, fresh);
    state.falSettings.apiKey = apiKey;
    state.input = input;
    state.backInput = backInput;
  },
} satisfies SliceCaseReducers<SpecsEditorSession>;
